import { useCallback, useMemo, useState } from 'react';
import { MdOutlineModeEditOutline } from 'react-icons/md';
import { ActionIcon, Button, Input, Text, Textarea, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import ModalForm from '../../../components/forms/ModalForm';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useFilters from '../../../hooks/useFilters';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { CallClassificationService } from '../../../services/calls/callClassificationService';

const initialForm = {
    classificationId: '',
    name: '',
    description: '',
    countsAsFailedVerificationCall: false,
};

const columnOrder = [
    'name',
    'description',
    'countsAsFailedVerificationCall',
    'createdAt',
    'updatedAt',
];

const CallClassificationsPage = () => {
    const service = new CallClassificationService();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const [form, setForm] = useState(initialForm);
    const [isSaving, setIsSaving] = useState(false);
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize } = useFilters({}, [{ field: 'name', order: 'asc' }], 1, 25);

    const [response, loading, error, refetch] = useFetch(useCallback(async () => {
        const apiResponse = await service.list(filters);

        return apiResponse.getResponseData();
    }, [filters]));

    const rows = useMemo(() => {
        if (Array.isArray(response?.data)) return response.data;
        if (Array.isArray(response)) return response;
        return [];
    }, [response]);

    const lastPage = response?.lastPage ?? 1;
    const isEditing = Boolean(form.classificationId);
    const canCreate = userCan('create_call_classifications', 'call_classifications');
    const canEdit = userCan('edit_call_classifications', 'call_classifications');
    const canDelete = userCan('delete_call_classifications', 'call_classifications');

    const closeFormModal = () => {
        setIsFormModalOpen(false);
        setForm(initialForm);
    };

    const formIsValid = useMemo(() => form.name.trim() !== '', [form.name]);

    const orderedData = () => rows.map((row: any) => {
        const orderedRow: any = { id: row.id };

        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });

        return orderedRow;
    });

    const handleSubmit = async () => {
        if (!formIsValid) {
            toast.error('Debes informar el nombre de la tipificación.');
            return;
        }

        setIsSaving(true);

        try {
            const payload = {
                ...form,
                name: form.name.trim(),
                description: form.description.trim() || null,
                countsAsFailedVerificationCall: Boolean(form.countsAsFailedVerificationCall),
            };

            const apiResponse = isEditing
                ? await (await service.edit(payload)).getResponseData()
                : await (await service.create(payload)).getResponseData();

            if (!apiResponse?.success) {
                handleErrors(apiResponse);
                return;
            }

            toast.success(isEditing ? 'Tipificación de llamada actualizada' : 'Tipificación de llamada creada');
            closeFormModal();
            refetch();
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (classificationId: string) => {
        try {
            const apiResponse = await (await service.delete(classificationId)).getResponseData();
            if (!apiResponse?.success) {
                handleErrors(apiResponse);
                return;
            }

            if (form.classificationId === classificationId) {
                closeFormModal();
            }

            toast.success('Tipificación de llamada eliminada');
            refetch();
        } catch (error) {
            handleErrors(error);
        }
    };

    if (loading && !response) {
        return <Loader height="50vh" />;
    }

    if (error && !loading) {
        return (
            <Page container="fluid">
                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    No se han podido cargar las tipificaciones de llamada.
                </div>
            </Page>
        );
    }

    return (
        <Page container="fluid">
            <CustomTable
                id="call-classifications-list-table"
                columnOrder={columnOrder}
                data={orderedData()}
                isLoading={loading}
                columnsNotShown={[]}
                overrideColumns={[
                    {
                        key: 'name',
                        render: (row: any) => <Text className="font-bold text-primary">{row.row.original.name || 'N/A'}</Text>,
                    },
                    {
                        key: 'description',
                        render: (row: any) => row.row.original.description || 'N/A',
                    },
                    {
                        key: 'countsAsFailedVerificationCall',
                        render: (row: any) => (row.row.original.countsAsFailedVerificationCall ? 'Sí' : 'No'),
                    },
                    {
                        key: 'createdAt',
                        render: (row: any) => row.row.original.createdAt?.date || row.row.original.createdAt || 'N/A',
                    },
                    {
                        key: 'updatedAt',
                        render: (row: any) => row.row.original.updatedAt?.date || row.row.original.updatedAt || 'N/A',
                    },
                ]}
                actions={[
                    {
                        label: 'Edit',
                        permissions: { group: 'call_classifications', permission: 'edit_call_classifications' },
                        render: (row: any) => (
                            <Tooltip size="sm" content="Editar tipificación de llamada" placement="top" color="invert">
                                <ActionIcon
                                    as="span"
                                    size="sm"
                                    variant="outline"
                                    className={`hover:!border-gray-900 hover:text-gray-700 ${!canEdit ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        setForm({
                                            classificationId: row.id,
                                            name: row.name ?? '',
                                            description: row.description ?? '',
                                            countsAsFailedVerificationCall: Boolean(row.countsAsFailedVerificationCall),
                                        });
                                        setIsFormModalOpen(true);
                                    }}
                                >
                                    <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                </ActionIcon>
                            </Tooltip>
                        ),
                    },
                    {
                        label: 'Delete',
                        permissions: { group: 'call_classifications', permission: 'delete_call_classifications' },
                        render: (row: any) => (
                            <Tooltip size="sm" content="Eliminar" placement="top" color="invert">
                                <div className={!canDelete ? 'pointer-events-none opacity-50' : ''}>
                                    <DeletePopover
                                        title="Eliminar tipificación de llamada"
                                        description={`¿Estás seguro de que deseas eliminar ${row.name}?`}
                                        onDelete={() => handleDelete(row.id)}
                                    />
                                </div>
                            </Tooltip>
                        ),
                    },
                ]}
                filters={filters}
                updateFilters={updateFilters}
                updateFilterOrder={updateFilterOrder}
                defaultOrder={filters.filter_order || undefined}
                paginationData={{
                    pageSize: filters.limit,
                    currentPage: filters.page,
                    pageCount: lastPage,
                    totalCount: response?.totalRegisters || rows.length,
                    handlePagination: updatePage,
                    handlePerPage: updatePageSize,
                }}
                filter={false}
            >
                <div className="flex items-center gap-2">
                    {canCreate && (
                        <Button onClick={() => setIsFormModalOpen(true)}>
                            Nueva tipificación
                        </Button>
                    )}
                    <Button variant="outline" onClick={() => refetch()}>
                        Actualizar
                    </Button>
                </div>
            </CustomTable>

            <ModalForm
                isOpen={isFormModalOpen}
                onClose={closeFormModal}
                title={isEditing ? 'Editar tipificación de llamada' : 'Nueva tipificación de llamada'}
                size="lg"
            >
                <div className="space-y-6">
                    <div className="grid gap-4">
                        <Input
                            label="Nombre"
                            value={form.name}
                            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                        />
                        <Textarea
                            label="Descripción"
                            value={form.description}
                            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                            rows={4}
                        />
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                                type="checkbox"
                                checked={form.countsAsFailedVerificationCall}
                                onChange={(event) => setForm((current) => ({ ...current, countsAsFailedVerificationCall: event.target.checked }))}
                            />
                            Cuenta para el conteo automático de fallo de verificación
                        </label>
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={closeFormModal}>
                            Cancelar
                        </Button>
                        <Button onClick={handleSubmit} disabled={isSaving || !formIsValid}>
                            {isSaving ? 'Guardando...' : (isEditing ? 'Guardar cambios' : 'Crear tipificación')}
                        </Button>
                    </div>
                </div>
            </ModalForm>
        </Page>
    );
};

export default CallClassificationsPage;
